import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { hostAPI } from '../services/api';
import { ChevronLeft, Mail, Loader2, RefreshCw, LogIn, Eye, EyeOff, KeyRound } from 'lucide-react';

const HostLoginPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('password');

  // Shared state
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  // Password tab
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // OTP tab
  const [otpStep, setOtpStep] = useState(1); // 1: Email, 2: OTP
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  // ============ PASSWORD LOGIN ============
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    try {
      setLoading(true);
      const response = await hostAPI.loginPassword({ email, password });
      if (response.success) {
        localStorage.setItem('hostId', response.host_id);
        localStorage.setItem('hostData', JSON.stringify(response.host));
        localStorage.setItem('hostToken', response.host_id);
        toast.success('Login successful!');
        navigate('/host/dashboard');
      }
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      if (status === 409) {
        // No password yet — nudge to OTP tab
        toast.info(message);
        setActiveTab('otp');
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============ OTP LOGIN ============
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const digits = [...otpDigits];
    digits[index] = value.slice(-1);
    setOtpDigits(digits);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits = ['', '', '', '', '', ''];
    pasted.split('').forEach((d, i) => { if (i < 6) newDigits[i] = d; });
    setOtpDigits(newDigits);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      setLoading(true);
      const response = await hostAPI.login({ email });
      if (response.success) {
        toast.success('OTP sent to your email!');
        setOtpStep(2);
        setResendCooldown(60);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    const otp = otpDigits.join('');
    if (otp.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }
    try {
      setLoading(true);
      const response = await hostAPI.loginVerify({ email, otp_code: otp });
      if (response.success) {
        localStorage.setItem('hostId', response.host_id);
        localStorage.setItem('hostData', JSON.stringify(response.host));
        localStorage.setItem('hostToken', response.host_id);

        // If host has no password yet, force them to set one
        if (response.requires_password_setup && response.setup_token) {
          toast.success('Login successful! Please set a password for future logins.');
          navigate(`/host/set-password?token=${encodeURIComponent(response.setup_token)}`);
          return;
        }

        toast.success('Login successful!');
        navigate('/host/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid OTP. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    try {
      setLoading(true);
      const response = await hostAPI.login({ email });
      if (response.success) {
        toast.success('OTP resent to your email!');
        setResendCooldown(60);
        setOtpDigits(['', '', '', '', '', '']);
      }
    } catch (error) {
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-md">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => (otpStep === 2 ? setOtpStep(1) : navigate('/'))}
            className="mb-4"
            data-testid="host-login-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {otpStep === 2 ? 'Back' : 'Back to home'}
          </Button>
        </div>

        <Card className="shadow-xl" data-testid="host-login-card">
          <CardHeader className="text-center">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#7CB342] to-[#6da036] flex items-center justify-center mx-auto mb-4">
              <LogIn className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Host Login</CardTitle>
            <CardDescription>Sign in to manage your homestays</CardDescription>
          </CardHeader>
          <CardContent>
            {otpStep === 2 ? (
              // OTP verification screen
              <div className="space-y-6" data-testid="host-otp-verify-section">
                <p className="text-sm text-center text-gray-600">
                  Enter the 6-digit code sent to<br />
                  <span className="font-semibold text-gray-900">{email}</span>
                </p>
                <div className="flex justify-center gap-2 sm:gap-3" onPaste={handleOtpPaste}>
                  {otpDigits.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (otpRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-[#7CB342] focus:ring-[#7CB342]"
                      data-testid={`host-login-otp-input-${index}`}
                    />
                  ))}
                </div>
                <Button
                  onClick={handleVerifyOTP}
                  disabled={loading || otpDigits.join('').length !== 6}
                  className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                  data-testid="host-login-verify-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying...</>
                  ) : 'Verify & Login'}
                </Button>
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2">Didn't receive the code?</p>
                  <Button
                    variant="ghost"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || loading}
                    className="text-[#7CB342] hover:text-[#6da036]"
                    data-testid="host-login-resend-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                  </Button>
                </div>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="password" data-testid="host-login-tab-password">
                    <KeyRound className="h-4 w-4 mr-2" />
                    Password
                  </TabsTrigger>
                  <TabsTrigger value="otp" data-testid="host-login-tab-otp">
                    <Mail className="h-4 w-4 mr-2" />
                    OTP
                  </TabsTrigger>
                </TabsList>

                {/* PASSWORD TAB */}
                <TabsContent value="password" className="space-y-4">
                  <form onSubmit={handlePasswordLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="email-pwd">Email Address</Label>
                      <Input
                        id="email-pwd"
                        type="email"
                        placeholder="your.email@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2"
                        data-testid="host-login-pwd-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="password">Password</Label>
                      <div className="relative mt-2">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pr-10"
                          data-testid="host-login-pwd-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          tabIndex={-1}
                          data-testid="host-login-pwd-toggle"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => navigate('/host/forgot-password')}
                        className="text-sm text-[#7CB342] hover:text-[#6da036] underline-offset-2 hover:underline"
                        data-testid="host-forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                      data-testid="host-login-pwd-submit-btn"
                    >
                      {loading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signing in...</>
                      ) : (
                        <><LogIn className="h-4 w-4 mr-2" />Login</>
                      )}
                    </Button>
                  </form>
                </TabsContent>

                {/* OTP TAB */}
                <TabsContent value="otp" className="space-y-4">
                  <form onSubmit={handleSendOTP} className="space-y-4">
                    <div>
                      <Label htmlFor="email-otp">Email Address</Label>
                      <Input
                        id="email-otp"
                        type="email"
                        placeholder="your.email@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2"
                        data-testid="host-login-otp-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                      data-testid="host-login-send-otp-btn"
                    >
                      {loading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending OTP...</>
                      ) : (
                        <><Mail className="h-4 w-4 mr-2" />Send Login OTP</>
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            )}

            <div className="text-center pt-6 mt-4 border-t">
              <p className="text-sm text-gray-500 mb-2">Don't have an account?</p>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/host/register')}
                className="w-full"
                data-testid="host-login-register-link"
              >
                Register as Host
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HostLoginPage;
